import { createContext, useContext, useEffect, useMemo } from "react";

import type { AccountEventType } from "@storyboard/web-client";
import type { Item } from "@/db/types";
import { useTeamAliases } from "@/hooks/useTeamAliases";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { unreadFeedIdsQuery } from "@/models/repositories";
import { handleFaviconChange, ipcRendererSend } from "@/utils";
import { AppContext } from "./AppStateProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type UnreadFeed = {
  feedId: string;
  unreadItems: Array<Item>;
};

export const unreadClearEvents: AccountEventType[] = [
  "Saw Feed Item",
  "Marked Feed as Read",
  "Opened Feed",
  "Started Listening to Feed Item", // used for in-line feed views
];

type UnreadsState = {
  allUnreadFeeds?: Array<UnreadFeed>;
  unreadFeedIds?: Array<string>;
};

export const UnreadsContext = createContext<UnreadsState>({
  allUnreadFeeds: [],
});

const UnreadsContextProvider = ({ children }) => {
  const { flags } = useContext(AppContext);
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { teamAliases } = useTeamAliases(workspaceId ?? "", myCurrentWorkspaceMembership?.id ?? "");

  const newDesign = flags.newDesign as boolean;

  const unreadFeedIds =
    useReactiveSelectQuery(
      unreadFeedIdsQuery,
      [
        {
          myCurrentWorkspaceMembershipId: myCurrentWorkspaceMembership?.id,
          teamAliases: newDesign && teamAliases.length > 0 ? teamAliases : undefined,
          requireNotificationPreference: true,
        },
      ],
      "UnreadsContextProvider:unreadFeedIdsQuery",
    ) ?? [];

  const mappedFeedIds = useMemo(() => unreadFeedIds?.map((ufi) => ufi.feedId) ?? [], [unreadFeedIds]);

  useEffect(() => {
    handleFaviconChange(mappedFeedIds);
    ipcRendererSend("update-count", mappedFeedIds.length);
  }, [mappedFeedIds]);

  const unreadsState: UnreadsState = {
    unreadFeedIds: mappedFeedIds,
  };

  return <UnreadsContext.Provider value={unreadsState}>{children}</UnreadsContext.Provider>;
};

export default UnreadsContextProvider;
